import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import ContainerHeader from "../../components/containerHeader";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../../components/loadingContainer";
import useAllUsers from "../../hooks/useAllUsers";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useState, useMemo, useEffect } from "react";
import CreateUserDialog from "../../dialogs/createUserDialog";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import { Users } from "../../api/models/user.models";
import EditUserDialog from "../../dialogs/editUserDialog";
import { MenuItemCommandEvent } from "primereact/menuitem";
import { confirmDialog } from "primereact/confirmdialog";
import AdminUserFilters from "../../components/adminUserFilters";
import AdminEditUserRolesDialog from "../../dialogs/adminEditUserRolesDialog";
import { useAdminDataContext } from "../../contexts/adminDataContext";
import usePipedriveIntegrations from "../../hooks/usePipedriveIntegrations";

const StyledTable = styled(DataTable)`
  & .edit-product-link {
    color: var(--sumi-blue);
    cursor: pointer;

    &:hover {
      color: var(--sumi-light-blue);
      text-decoration: underline;
    }
  }

  .p-datatable-tbody > tr > td {
    border: none;
    background-color: #fdfdfd;
    border-bottom: 1px solid #e1e1e1;
    padding: 0.5rem 1rem;
  }

  .p-datatable-thead > tr > th {
    border: none;
    font-weight: 500;
    font-size: 0.8rem;
    color: #888888;
    text-transform: uppercase;
  }

  // rotate svg icon
  & .p-row-toggler svg {
    color: var(--text-color);
    transform: rotate(90deg);
  }

  // rotate 180 degrees when attribute expanded is true
  & .p-row-toggler[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }

  .p-datatable-row-expansion {
    > td {
      padding: 0;
      border: none;
    }

    & .p-datatable-tbody > tr > td {
      background-color: var(--sumi-background-blue);
    }

    & .p-datatable-tbody > tr > td {
      border: none;
    }
  }
`;

const AddUserButton = styled(Button)`
  margin-left: 0;
  position: absolute;
  right: 0;
`;

const AdminUsersContainer = styled.div`
  position: relative;
  padding-bottom: 4rem;
`;

const TableContainer = styled.div`
  padding: 1rem 2rem;
`;

interface UserFilters {
  name?: string;
  email?: string;
  companyId?: number;
  companyCode?: string;
}

const AdminUsers: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // Get companyId from query string if present
  const companyIdFromQuery = searchParams.get("companyId");
  const initialFilters: UserFilters = companyIdFromQuery
    ? { companyId: parseInt(companyIdFromQuery, 10) }
    : {};

  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showEditUserRolesDialog, setShowEditUserRolesDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Users | undefined>(
    undefined,
  );
  const [filters, setFilters] = useState<UserFilters>(initialFilters);
  const [debouncedFilters, setDebouncedFilters] =
    useState<UserFilters>(initialFilters);
  const [page, setPage] = useState<number>(1);
  const [rows, setRows] = useState<number>(25);

  const { companies, isLoadingCompanies } = useAdminDataContext();

  // Debounce filters
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1); // Reset to first page when filters change
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [filters]);

  const {
    users,
    totalUsers,
    usersIsLoading,
    createNewUser,
    savingNewUser,
    sendUserInvite,
    sendingUserInvite,
    updateUser,
    updatingUser,
    deleteUser,
  } = useAllUsers({
    skip: (page - 1) * rows,
    take: rows,
    name: debouncedFilters.name,
    email: debouncedFilters.email,
    companyId: debouncedFilters.companyId,
    companyCode: debouncedFilters.companyCode,
    enabled: true,
  });

  const { updatePipeDriveInfo, isUpdatingPipeDriveInfo } =
    usePipedriveIntegrations();

  const handleAddUserClick = () => {
    setShowAddUserDialog(true);
  };

  const handleFiltersChange = (newFilters: UserFilters) => {
    setFilters(newFilters);
  };

  const getMenuItems = (user: Users) => {
    const items = [
      {
        label: t("cmd-edit-user"),
        icon: "pi pi-pencil",
        command: (e: MenuItemCommandEvent) => {
          opRefs.current[user.id]?.hide();
          setShowEditUserDialog(true);
          setSelectedUser(user);
        },
      },
      {
        label: t("cmd-view-user-roles"),
        icon: "pi pi-users",
        command: () => {
          opRefs.current[user.id]?.hide();
          setShowEditUserRolesDialog(true);
          setSelectedUser(user);
        },
      },
    ];

    // Insert refresh user button if externalId exists
    if (!!user.externalIds?.length) {
      items.push({
        label: t("cmd-refresh-user"),
        icon: "pi pi-refresh",
        command: () => {
          opRefs.current[user.id]?.hide();
          // Call your refresh logic here, e.g. updatePipedriveUser(user.id)
          updatePipeDriveInfo({
            userId: user.id,
          });
        },
      });
    }

    items.push({
      label: t("cmd-delete-user"),
      icon: "pi pi-trash",
      command: () => {
        opRefs.current[user.id]?.hide();
        confirmDialog({
          showHeader: false,
          message: t("are-you-sure-you-want-remove-user", {
            name: user.names.firstName + " " + user.names.lastName,
          }),
          acceptLabel: t("cmd-delete"),
          rejectLabel: t("cmd-cancel"),
          icon: "pi pi-exclamation-triangle",
          headerClassName: "p-confirm-dialog-header-danger",
          acceptClassName: "p-button-danger",
          accept: () => {
            deleteUser(user.id);
          },
          style: { minWidth: "300px" },
        });
      },
    });

    return items;
  };

  const opRefs = React.useRef<{ [key: number]: OverlayPanel | null }>({});

  const menuColumnTemplate = (rowData: Users) => {
    return (
      <>
        <Button
          icon="pi pi-ellipsis-v"
          rounded
          text
          aria-label="More"
          onClick={(e) => {
            if (!opRefs.current[rowData.id]) return;
            opRefs.current[rowData.id]?.toggle(e);
          }}
        />
        <OverlayPanel ref={(el) => (opRefs.current[rowData.id] = el)}>
          <Menu model={getMenuItems(rowData)} />
        </OverlayPanel>
      </>
    );
  };

  return (
    <AdminUsersContainer>
      <ContainerHeader
        title={t("header-all-users-superadmin")}
        description="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
      />

      <LoadingContainer
        status={
          isLoadingCompanies
            ? HttpRequestStatus.LOADING
            : HttpRequestStatus.SUCCESS
        }
      >
        <div
          style={{
            display: "flex",
            margin: "20px",
            width: "96%",
            position: "relative",
          }}
        >
          <AdminUserFilters
            onFiltersChange={handleFiltersChange}
            companies={companies || []}
            filters={filters}
            debouncedFilters={debouncedFilters}
            setFilters={setFilters}
            setDebouncedFilters={setDebouncedFilters}
          />
          <AddUserButton
            id="add-user-button"
            onClick={handleAddUserClick}
            icon="pi pi-user-plus"
            label={t("label-add-new-user")}
          />
        </div>
        <LoadingContainer
          status={
            usersIsLoading
              ? HttpRequestStatus.LOADING
              : HttpRequestStatus.SUCCESS
          }
        >
          <TableContainer>
            {showAddUserDialog && (
              <CreateUserDialog
                show={showAddUserDialog}
                onHide={() => setShowAddUserDialog(false)}
                createNewUser={createNewUser}
                savingNewUser={savingNewUser}
                sendingUserInvite={sendingUserInvite}
                sendUserInvite={sendUserInvite}
                companies={companies || []}
              />
            )}
            {showEditUserDialog && selectedUser && (
              <EditUserDialog
                show={showEditUserDialog}
                onHide={() => setShowEditUserDialog(false)}
                updateUser={updateUser}
                updatingUser={updatingUser}
                user={selectedUser}
              />
            )}
            {showEditUserRolesDialog && selectedUser && (
              <AdminEditUserRolesDialog
                show={showEditUserRolesDialog}
                onHide={() => setShowEditUserRolesDialog(false)}
                user={selectedUser}
                companies={companies || []}
              />
            )}
            <StyledTable
              value={users || []}
              lazy
              paginator
              first={(page - 1) * rows}
              rows={rows}
              totalRecords={totalUsers}
              onPage={(e) => {
                const newPage = Math.floor(e.first / e.rows) + 1;
                if (newPage !== page) {
                  setPage(newPage);
                }
                if (e.rows !== rows) {
                  setRows(e.rows);
                }
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate={`{first} - {last} / ${totalUsers}`}
              emptyMessage={t("label-no-users")}
              loading={usersIsLoading}
            >
              <Column field="names.firstName" header={t("label-first-name")} />
              <Column field="names.lastName" header={t("label-last-name")} />
              <Column field="email.email" header={t("label-email")} />
              <Column
                field="phoneNumber.number"
                header={t("label-phone-number")}
              />
              <Column
                body={menuColumnTemplate}
                style={{ width: "3rem", textAlign: "center" }}
              />
            </StyledTable>
          </TableContainer>
        </LoadingContainer>
      </LoadingContainer>
    </AdminUsersContainer>
  );
};

export default AdminUsers;
